import { useState, FC } from "react";

// Components
import Button from "../../../components/Desktop/Button";

// Icons
import { ReactComponent as FaqDownArrowIcon } from "../../../icons/FaqDownArrow.svg";
import { ReactComponent as FaqUpArrowIcon } from "../../../icons/FaqUpArrow.svg";

const HomeFaq = () => {
  const [isFoodEntrepreneur, setIsFoodEntrepreneur] = useState(true);

  function toggleFoodEntrepreneur() {
    setIsFoodEntrepreneur((prev) => !prev);
  }

  return (
    <section className="px-[120px] py-[100px] bg-[#F8F8F8]">
      {/* Header */}
      <div className="text-center mb-[48px]">
        {/* Headings */}
        <div className="mb-[24px]">
          <p className="text-[16px] font-open-sans font-semibold text-primaryDark-0 mb-[8px]">
            FAQs
          </p>
          <h3 className="text-[36px] font-raleway font-bold leading-tight text-primaryDark-0">
            <span className="text-primaryDark-400"> Your Questions </span>{" "}
            Answered.
          </h3>
        </div>

        {/* Buttons */}
        {isFoodEntrepreneur ? (
          <HomeFoodEntrepreneursButtons
            onClick={toggleFoodEntrepreneur}
            disabled={isFoodEntrepreneur}
          />
        ) : (
          <HomeResourceProvidersButtons
            onClick={toggleFoodEntrepreneur}
            disabled={!isFoodEntrepreneur}
          />
        )}
      </div>

      {/* Questions */}
      {isFoodEntrepreneur ? (
        <HomeFoodEntrepreneursFaqQuestion />
      ) : (
        <HomeResourceProvidersFaqQuestion />
      )}

      {/* Bottom Button */}
      <div className="flex items-center justify-center">
        <Button label="I Still have More Questions" variant="secondary" />
      </div>
    </section>
  );
};

type HomeButtonsProps = {
  onClick: () => void;
  disabled: boolean;
};

const HomeFoodEntrepreneursButtons: FC<HomeButtonsProps> = ({
  onClick,
  disabled,
}) => {
  return (
    <div className="flex items-center gap-[16px] justify-center">
      <Button
        label="For Food Entrepreneurs"
        variant="secondary"
        faqButton
        onClick={onClick}
        disabled={disabled}
        isDisabled
      />
      <Button
        label="For Resource Providers"
        variant="primary"
        faqButton
        onClick={onClick}
      />
    </div>
  );
};

const HomeResourceProvidersButtons: FC<HomeButtonsProps> = ({
  onClick,
  disabled,
}) => {
  return (
    <div className="flex items-center gap-[16px] justify-center">
      <Button
        label="For Food Entrepreneurs"
        variant="primary"
        faqButton
        onClick={onClick}
      />
      <Button
        label="For Resource Providers"
        variant="secondary"
        faqButton
        onClick={onClick}
        disabled={disabled}
        isDisabled
      />
    </div>
  );
};

const HomeFoodEntrepreneursFaqQuestion = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div className="bg-text-1000 px-[20px] py-[20px] w-[532.66px] mx-auto rounded-[16px] mb-[24px]">
      <HomeFaqQuestionItem
        index={0}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="What is HomeCook?"
        answer="HomeCook is a platform that connects food entrepreneurs with commercial kitchens, tools, and resources they need to start or grow their food business."
      />

      <HomeFaqQuestionItem
        index={1}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="Who can join as a food entrepreneur?"
        answer="Anyone passionate about cooking or running a food business - including home cooks, caterers, bakers, food vendors, and startup food brands."
      />

      <HomeFaqQuestionItem
        index={2}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="What is the Resource Pantry?"
        answer="The Resource Pantry is your dashboard inside HomeCook where you can discover available kitchens, culinary tools, storage options, and other business resources. Click here to view the Resource Pantry"
      />

      <HomeFaqQuestionItem
        index={3}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="Do I need a registered business to join?"
        answer="No. You can join as an individual. However, depending on your scale and local regulations, business registration may be required later."
      />

      <HomeFaqQuestionItem
        index={4}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="How do I access a culinary resource?"
        answer="After creating an account, you can browse available kitchens, view their details, and request access based on availability and requirements."
      />

      <HomeFaqQuestionItem
        index={5}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="Can I book culinary resources for specific times?"
        answer="Yes. Kitchens can be booked based on available time slots, allowing you to work flexibly around your schedule."
      />

      <HomeFaqQuestionItem
        index={6}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="What types of culinary resources are available?"
        answer="Options may include commercial kitchens, shared kitchens, restaurant kitchens during off-hours, bakery spaces, and specialty cooking facilities."
      />

      <HomeFaqQuestionItem
        index={7}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="Does HomeCook provide equipment too?"
        answer="Some kitchens come fully equipped, while others may offer additional tools or storage for a fee. Details are listed for each kitchen."
      />

      <HomeFaqQuestionItem
        index={8}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="How much does it cost to use a culinary resource?"
        answer="Pricing varies depending on the kitchen, equipment, duration, and services included."
      />

      <HomeFaqQuestionItem
        index={9}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="Can I grow my food business through HomeCook?"
        answer="Yes. HomeCook is designed to remove infrastructure barriers so you can focus on production, sales, and scaling your brand."
      />
    </div>
  );
};

const HomeResourceProvidersFaqQuestion = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div className="bg-text-1000 px-[20px] py-[20px] w-[532.66px] mx-auto rounded-[16px] mb-[24px]">
      <HomeFaqQuestionItem
        index={0}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="What is HomeCook for resource providers?"
        answer="HomeCook helps you monetize your unused kitchen space by connecting you with vetted food entrepreneurs who need licensed cooking facilities."
      />

      <HomeFaqQuestionItem
        index={1}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="Who can list a culinary resource?"
        answer="Restaurant owners, bakery operators, culinary schools, food businesses, hotels, churches, or anyone with a suitable commercial kitchen."
      />

      <HomeFaqQuestionItem
        index={2}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="How do I get started as a resource provider?"
        answer="Simply reach out to HomeCook by clicking here to express interest. Our team will guide you through onboarding and listing your space."
      />

      <HomeFaqQuestionItem
        index={3}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="When can my culinary resource be used?"
        answer="You control availability - list only the hours or days when your culinary resource is not in use."
      />

      <HomeFaqQuestionItem
        index={4}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="Do I need special permits to share my culinary resource?"
        answer="Requirements vary by location. HomeCook will advise you on any necessary compliance or documentation."
      />

      <HomeFaqQuestionItem
        index={5}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="Who uses my culinary resource?"
        answer="Food entrepreneurs such as caterers, bakers, meal prep businesses, and small food brands looking for licensed cooking space."
      />

      <HomeFaqQuestionItem
        index={6}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="Can I approve who uses my culinary resource?"
        answer="Yes. You retain control and can approve requests based on your preferences and policies."
      />

      <HomeFaqQuestionItem
        index={7}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="How do I earn from my culinary resource?"
        answer="You earn rental income based on the pricing you set and the hours booked."
      />

      <HomeFaqQuestionItem
        index={8}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="What about damage or misuse?"
        answer="HomeCook establishes guidelines and agreements to protect resource providers and ensure responsible use."
      />

      <HomeFaqQuestionItem
        index={9}
        openIndex={openIndex}
        onToggle={handleToggle}
        question="Why partner with HomeCook?"
        answer="HomeCook helps you generate additional revenue, support local food businesses, and maximize the value of your existing assets."
      />
    </div>
  );
};

type HomeFaqQuestionItemProps = {
  index: number;
  openIndex: number | null;
  onToggle: (index: number) => void;
  question: string;
  answer: string;
};

const HomeFaqQuestionItem: FC<HomeFaqQuestionItemProps> = ({
  index,
  openIndex,
  onToggle,
  question,
  answer,
}) => {
  const isAnswerBoxVisible = openIndex === index;
  const isInactive = openIndex !== null && openIndex !== index;

  return (
    <div
      className={`w-full transition-all ${!isAnswerBoxVisible ? "hover:scale-95" : ""}`}
    >
      <div
        className="flex items-center justify-between gap-[8px] w-full px-[10px] py-[15px] cursor-pointer"
        onClick={() => onToggle(index)}
      >
        <div className="w-[calc(100%-20px)]">
          <p
            className={`text-[16px] font-open-sans font-medium transition-colors ${isInactive ? "text-text-800" : "text-text-0"}`}
          >
            {question}
          </p>

          {isAnswerBoxVisible && (
            <p className="text-[16px] text-text-0 mt-[8px] font-open-sans font-medium leading-loose max-w-[800px]">
              {answer}
            </p>
          )}
        </div>

        {isAnswerBoxVisible ? (
          <FaqUpArrowIcon
            className={`w-[20px] h-[20px] transition-colors ${isInactive ? "text-text-800" : "text-primaryDark-400"}`}
          />
        ) : (
          <FaqDownArrowIcon
            className={`w-[20px] h-[20px] transition-colors ${isInactive ? "text-text-800" : "text-primaryDark-400"}`}
          />
        )}
      </div>
    </div>
  );
};

export default HomeFaq;
